/**
 * VendorGalleryPanel — cover + gallery uploads for the vendor.
 * - "Cover photo" is stored on the vendor doc's `cover` field (used as hero).
 * - "Gallery photos" are stored in vendor_settings.gallery_images[].
 * - Drag-drop tiles to reorder; AI auto-caption via gpt-5.2 vision.
 * Uploads reuse the existing /api/files endpoint (5MB, image mime only).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, Upload, Loader2, Trash2, Layout, Sparkles, GripVertical } from 'lucide-react';
import api, { vendorAdminApi } from '../services/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const uploadFile = async (file) => {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post('/files', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return data.url;
};

const validateImage = (file) => {
  if (!/^image\//.test(file.type)) return 'Please pick an image file';
  if (file.size > 5 * 1024 * 1024) return 'Image is larger than 5 MB';
  return null;
};

const VendorGalleryPanel = ({ vendorId, impersonating }) => {
  const vid = impersonating ? vendorId : undefined;
  const [store, setStore] = useState(null);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState({ cover: false, gallery: false });
  const [captioning, setCaptioning] = useState({}); // { [imageId]: true }
  const [dragId, setDragId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [reordering, setReordering] = useState(false);
  const coverInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const load = async () => {
    try {
      const [s, g] = await Promise.all([
        vendorAdminApi.store(vid),
        api.get('/vendor-admin/gallery', { params: vid ? { vendor_id: vid } : undefined }).then((r) => r.data),
      ]);
      setStore(s);
      setImages(g.images || []);
    } catch (e) {
      toast.error('Failed to load gallery');
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vid]);

  const onPickCover = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const err = validateImage(file);
    if (err) { toast.error(err); return; }
    setUploading((u) => ({ ...u, cover: true }));
    try {
      const url = await uploadFile(file);
      await vendorAdminApi.updateStore({ cover: url }, vid);
      setStore((s) => ({ ...s, cover: url }));
      toast.success('Cover photo updated');
    } catch (er) {
      toast.error(er?.response?.data?.detail || 'Cover upload failed');
    } finally {
      setUploading((u) => ({ ...u, cover: false }));
    }
  };

  const onPickGallery = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    setUploading((u) => ({ ...u, gallery: true }));
    try {
      for (const file of files) {
        const err = validateImage(file);
        if (err) { toast.error(`${file.name}: ${err}`); continue; }
        const url = await uploadFile(file);
        const { data } = await api.post(
          '/vendor-admin/gallery',
          { url },
          { params: vid ? { vendor_id: vid } : undefined },
        );
        setImages((prev) => [...prev, data]);
      }
      toast.success('Gallery updated');
    } catch (er) {
      toast.error(er?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading((u) => ({ ...u, gallery: false }));
    }
  };

  const removeImage = async (imageId) => {
    try {
      await api.delete(`/vendor-admin/gallery/${imageId}`, {
        params: vid ? { vendor_id: vid } : undefined,
      });
      setImages((prev) => prev.filter((i) => i.id !== imageId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const persistOrder = async (nextImages) => {
    setReordering(true);
    try {
      await api.patch(
        '/vendor-admin/gallery/reorder',
        { image_ids: nextImages.map((i) => i.id) },
        { params: vid ? { vendor_id: vid } : undefined },
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reorder failed');
      load();
    } finally {
      setReordering(false);
    }
  };

  const handleDrop = (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); setDropTarget(null); return; }
    setImages((prev) => {
      const from = prev.findIndex((i) => i.id === dragId);
      const to = prev.findIndex((i) => i.id === targetId);
      if (from < 0 || to < 0) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      persistOrder(next);
      return next;
    });
    setDragId(null);
    setDropTarget(null);
  };

  const autoCaption = async (imageId) => {
    setCaptioning((c) => ({ ...c, [imageId]: true }));
    try {
      const { data } = await api.post(
        `/vendor-admin/gallery/${imageId}/auto-caption`,
        {},
        { params: vid ? { vendor_id: vid } : undefined },
      );
      setImages((prev) => prev.map((i) => (i.id === imageId ? { ...i, caption: data.caption } : i)));
      toast.success(`AI caption: "${data.caption}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Auto-caption failed');
    } finally {
      setCaptioning((c) => ({ ...c, [imageId]: false }));
    }
  };

  const saveCaption = async (imageId, caption) => {
    try {
      await api.patch(
        `/vendor-admin/gallery/${imageId}/caption`,
        { caption },
        { params: vid ? { vendor_id: vid } : undefined },
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Caption save failed');
    }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-5 mb-6" data-testid="vendor-gallery-panel">
      <div className="flex items-center gap-2 mb-1">
        <Layout className="h-5 w-5 text-emerald-600" />
        <h2 className="font-semibold text-slate-900">Storefront gallery</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Your cover photo is the hero image at the top of your storefront. Gallery photos appear in your public gallery grid (menu-item photos are pulled automatically).
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Cover */}
        <div className="md:col-span-1">
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Cover / hero</div>
          <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={onPickCover} data-testid="cover-file-input" />
          {store?.cover ? (
            <div className="relative rounded-xl overflow-hidden border border-slate-200 bg-slate-100 aspect-[4/3]">
              <img src={store.cover} alt="Cover" className="w-full h-full object-cover" data-testid="cover-preview" />
              <button
                onClick={() => coverInputRef.current?.click()}
                disabled={uploading.cover}
                className="absolute bottom-2 right-2 bg-slate-900/85 text-white text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1 hover:bg-slate-900"
                data-testid="cover-replace-btn"
              >
                {uploading.cover ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                Replace
              </button>
            </div>
          ) : (
            <button
              onClick={() => coverInputRef.current?.click()}
              disabled={uploading.cover}
              className="w-full aspect-[4/3] rounded-xl border-2 border-dashed border-slate-300 hover:border-emerald-400 hover:bg-emerald-50/40 flex flex-col items-center justify-center text-slate-500 text-sm gap-1"
              data-testid="cover-upload-btn"
            >
              {uploading.cover ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImageIcon className="h-6 w-6" />}
              Upload cover photo
              <span className="text-[11px] text-slate-400">Recommended 1200×900</span>
            </button>
          )}
        </div>

        {/* Gallery */}
        <div className="md:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
              Gallery ({images.length})
            </div>
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={onPickGallery}
              data-testid="gallery-file-input"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => galleryInputRef.current?.click()}
              disabled={uploading.gallery}
              data-testid="gallery-add-btn"
            >
              {uploading.gallery ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
              Add photos
            </Button>
          </div>
          {images.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-slate-200 p-6 text-center text-sm text-slate-400" data-testid="gallery-empty">
              No gallery photos yet — click <b>Add photos</b> to upload up to 5 MB each.
            </div>
          ) : (
            <>
              <div className="text-[11px] text-slate-500 mb-2 flex items-center gap-1">
                <GripVertical className="h-3 w-3" /> Drag tiles to reorder{reordering ? ' · saving…' : ''}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="gallery-thumbnails">
                {images.map((img) => {
                  const isCaptioning = !!captioning[img.id];
                  const isDropTarget = dropTarget === img.id && dragId !== img.id;
                  return (
                    <div
                      key={img.id}
                      draggable
                      onDragStart={() => setDragId(img.id)}
                      onDragOver={(e) => { e.preventDefault(); setDropTarget(img.id); }}
                      onDragLeave={() => setDropTarget((t) => (t === img.id ? null : t))}
                      onDrop={(e) => { e.preventDefault(); handleDrop(img.id); }}
                      onDragEnd={() => { setDragId(null); setDropTarget(null); }}
                      className={`group relative rounded-lg overflow-hidden border ${
                        isDropTarget ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-slate-200'
                      } bg-white cursor-grab active:cursor-grabbing`}
                      data-testid={`gallery-tile-${img.id}`}
                    >
                      <div className="aspect-square bg-slate-100 relative">
                        <img src={img.url} alt={img.caption || 'Gallery'} className="w-full h-full object-cover" />
                        <div className="absolute top-1 left-1 bg-black/60 text-white/90 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                          <GripVertical className="h-3 w-3" />
                        </div>
                        <button
                          onClick={() => removeImage(img.id)}
                          className="absolute top-1 right-1 bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity rounded-full h-6 w-6 flex items-center justify-center hover:bg-rose-600"
                          aria-label="Delete photo"
                          data-testid={`gallery-delete-${img.id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                      <div className="p-2 space-y-1.5">
                        <input
                          key={`caption-${img.id}-${img.caption || ''}`}
                          type="text"
                          defaultValue={img.caption || ''}
                          onBlur={(e) => {
                            const v = e.target.value.trim();
                            if (v !== (img.caption || '')) {
                              setImages((prev) => prev.map((i) => (i.id === img.id ? { ...i, caption: v } : i)));
                              saveCaption(img.id, v);
                            }
                          }}
                          placeholder="Caption…"
                          className="w-full text-xs px-2 py-1 border border-slate-200 rounded-md focus:outline-none focus:border-emerald-500"
                          data-testid={`gallery-caption-${img.id}`}
                        />
                        <button
                          type="button"
                          onClick={() => autoCaption(img.id)}
                          disabled={isCaptioning}
                          className="w-full inline-flex items-center justify-center gap-1 text-[11px] font-medium text-emerald-700 hover:text-emerald-800 disabled:opacity-50"
                          data-testid={`gallery-autocaption-${img.id}`}
                        >
                          {isCaptioning
                            ? <><Loader2 className="h-3 w-3 animate-spin" /> Generating…</>
                            : <><Sparkles className="h-3 w-3" /> Auto-caption with AI</>}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default VendorGalleryPanel;
